import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ColorField,
  ErrorText,
  Field,
  Input,
  LocalizedInput,
  Select,
  type Localized,
} from '@/components/ui';

interface Size {
  id: string;
  value: string;
  sortOrder: number;
  isActive: boolean;
}

interface SizeGroup {
  id: string;
  code: string;
  name: Localized;
  sizes: Size[];
}

interface Color {
  id: string;
  name: Localized;
  hex: string;
  isActive: boolean;
}

export default function DictionariesPage() {
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Справочники</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Размеры, цвета и бренды — общие для всех товаров, задаются один раз
        </p>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <Sizes onError={setError} />
      <Colors onError={setError} />
      <Brands onError={setError} />
    </div>
  );
}

function Sizes({ onError }: { onError: (message: string | null) => void }) {
  const queryClient = useQueryClient();
  const [groupId, setGroupId] = useState('');
  const [value, setValue] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });

  const { data: groups = [] } = useQuery({
    queryKey: ['size-groups'],
    queryFn: async () => (await api.get<SizeGroup[]>('/admin/size-groups')).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['size-groups'] });
  const fail = (e: unknown) => onError(errorMessage(e));

  const addOne = useMutation({
    mutationFn: () => api.post('/admin/sizes', { groupId: groupId || groups[0]?.id, value }),
    onSuccess: () => {
      setValue('');
      onError(null);
      void invalidate();
    },
    onError: fail,
  });

  const addRange = useMutation({
    mutationFn: () =>
      api.post('/admin/sizes/range', {
        groupId: groupId || groups[0]?.id,
        from: Number(range.from),
        to: Number(range.to),
      }),
    onSuccess: () => {
      setRange({ from: '', to: '' });
      onError(null);
      void invalidate();
    },
    onError: fail,
  });

  const toggle = useMutation({
    mutationFn: (size: Size) => api.patch(`/admin/sizes/${size.id}`, { isActive: !size.isActive }),
    onSuccess: invalidate,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/sizes/${id}`),
    onSuccess: () => {
      onError(null);
      void invalidate();
    },
    onError: fail,
  });

  return (
    <Card title="Размеры">
      <div className="space-y-5">
        {groups.map((group) => (
          <div key={group.id}>
            <p className="mb-2 text-sm font-medium">
              {group.name.ru} <span className="text-neutral-400">({group.sizes.length})</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {group.sizes.map((size) => (
                <span
                  key={size.id}
                  className={`group flex items-center gap-1 rounded-lg border px-2 py-1 text-xs ${
                    size.isActive
                      ? 'border-neutral-300 dark:border-neutral-700'
                      : 'border-dashed border-neutral-300 text-neutral-400 dark:border-neutral-700'
                  }`}
                >
                  {size.value}
                  <button
                    onClick={() => toggle.mutate(size)}
                    className="opacity-0 transition group-hover:opacity-100"
                    title={size.isActive ? 'Отключить' : 'Включить'}
                  >
                    {size.isActive ? '◉' : '○'}
                  </button>
                  <button
                    onClick={() => remove.mutate(size.id)}
                    className="text-red-500 opacity-0 transition group-hover:opacity-100"
                    title="Удалить"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>
        ))}

        <div className="grid gap-3 border-t border-neutral-200 pt-4 sm:grid-cols-4 dark:border-neutral-800">
          <Field label="Группа">
            <Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name.ru}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Одно значение">
            <div className="flex gap-2">
              <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="XXXL" />
              <Button onClick={() => addOne.mutate()} disabled={!value}>
                +
              </Button>
            </div>
          </Field>

          <Field label="Диапазон" hint="Например, расширить обувь до 55">
            <div className="flex gap-2">
              <Input
                value={range.from}
                onChange={(e) => setRange({ ...range, from: e.target.value })}
                placeholder="51"
                inputMode="numeric"
              />
              <Input
                value={range.to}
                onChange={(e) => setRange({ ...range, to: e.target.value })}
                placeholder="55"
                inputMode="numeric"
              />
              <Button onClick={() => addRange.mutate()} disabled={!range.from || !range.to}>
                +
              </Button>
            </div>
          </Field>
        </div>
      </div>
    </Card>
  );
}

function Colors({ onError }: { onError: (message: string | null) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState<Localized>({});
  const [hex, setHex] = useState('#000000');

  const { data: colors = [] } = useQuery({
    queryKey: ['colors'],
    queryFn: async () => (await api.get<Color[]>('/admin/colors')).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['colors'] });
  const fail = (e: unknown) => onError(errorMessage(e));

  const create = useMutation({
    mutationFn: () => api.post('/admin/colors', { name, hex }),
    onSuccess: () => {
      setName({});
      onError(null);
      void invalidate();
    },
    onError: fail,
  });

  const toggle = useMutation({
    mutationFn: (color: Color) =>
      api.patch(`/admin/colors/${color.id}`, { isActive: !color.isActive }),
    onSuccess: invalidate,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/colors/${id}`),
    onSuccess: () => {
      onError(null);
      void invalidate();
    },
    onError: fail,
  });

  return (
    <Card title="Цвета">
      <div className="space-y-5">
        <div className="flex flex-wrap gap-2">
          {colors.map((color) => (
            <div
              key={color.id}
              className="group flex items-center gap-2 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700"
            >
              <span
                className="size-4 rounded-full border border-neutral-300 dark:border-neutral-600"
                style={{ background: color.hex }}
              />
              <span className={color.isActive ? '' : 'text-neutral-400 line-through'}>
                {color.name.ru}
              </span>
              {!color.isActive && <Badge tone="red">выкл</Badge>}
              <button
                onClick={() => toggle.mutate(color)}
                className="opacity-0 group-hover:opacity-100"
              >
                {color.isActive ? '◉' : '○'}
              </button>
              <button
                onClick={() => remove.mutate(color.id)}
                className="text-red-500 opacity-0 group-hover:opacity-100"
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div className="space-y-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <LocalizedInput label="Новый цвет" value={name} onChange={setName} />
          <div className="flex items-end gap-3">
            <Field label="Оттенок">
              <ColorField value={hex} onChange={setHex} className="w-40" />
            </Field>
            <Button onClick={() => create.mutate()} disabled={!name.ru && !name.uz && !name.en}>
              Добавить цвет
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

interface Brand {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
}

/**
 * Бренды — справочник, а не строка у товара: иначе «Gucci», «gucci» и «GUCCI»
 * разъедутся, и фильтр каталога по бренду перестанет собирать линейку.
 */
function Brands({ onError }: { onError: (message: string | null) => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');

  const { data: brands = [] } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => (await api.get<Brand[]>('/admin/brands')).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['brands'] });
  const fail = (e: unknown) => onError(errorMessage(e));

  const create = useMutation({
    mutationFn: () => api.post('/admin/brands', { name }),
    onSuccess: () => {
      setName('');
      onError(null);
      void invalidate();
    },
    onError: fail,
  });

  const toggle = useMutation({
    mutationFn: (brand: Brand) =>
      api.patch(`/admin/brands/${brand.id}`, { isActive: !brand.isActive }),
    onSuccess: invalidate,
    onError: fail,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/admin/brands/${id}`),
    onSuccess: () => {
      onError(null);
      void invalidate();
    },
    onError: fail,
  });

  return (
    <Card title="Бренды">
      <div className="space-y-5">
        {brands.length === 0 ? (
          <p className="text-sm text-neutral-500">Брендов пока нет</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {brands.map((brand) => (
              <div
                key={brand.id}
                className="group flex items-center gap-2 rounded-lg border border-neutral-300 px-2 py-1.5 text-sm dark:border-neutral-700"
              >
                <span className={brand.isActive ? '' : 'text-neutral-400 line-through'}>
                  {brand.name}
                </span>
                {!brand.isActive && <Badge tone="red">выкл</Badge>}
                <button
                  onClick={() => toggle.mutate(brand)}
                  className="opacity-0 group-hover:opacity-100"
                  title={brand.isActive ? 'Скрыть' : 'Включить'}
                >
                  {brand.isActive ? '◉' : '○'}
                </button>
                <button
                  onClick={() => remove.mutate(brand.id)}
                  className="text-red-500 opacity-0 group-hover:opacity-100"
                  title="Удалить"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <Field label="Новый бренд" hint="Собственное имя, не переводится">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Elegance"
              className="w-56"
            />
          </Field>
          <Button disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>
            Добавить бренд
          </Button>
        </div>
      </div>
    </Card>
  );
}
